import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Form } from '@/components/form/Form';
import { Property, SelectField } from '@/components/form/fields/select/SelectInput';
import { EmailField } from '@/components/form/fields/text/EmailInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { Multistep } from '@/components/multistep/Multistep';
import { Step } from '@/components/multistep/Questionnaire';
import { ToggleInput } from '@/components/form/fields/toggle/Toggle';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { PageLoading } from '@/layouts/PageLoading';
import { Container } from '@/layouts/main/Main';
import { Address } from '@/services/api/commonTypes';
import { useUploadLogo } from '@/services/api/hooks/images';
import { useInviteToPartner } from '@/services/api/hooks/invites';
import { useCreatePartner, useCreatePartnerCode } from '@/services/api/hooks/partners';
import { useRegions } from '@/services/api/hooks/region';
import { RoleType, useUser } from '@/services/authentication/hooks/user';
import { PageTitle } from '@/utils/Title';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, Suspense, useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import AddressField from '../../components/form/fields/address/AddressField';
import { serialiseAddress } from '../onboarding/commonSteps/addressHelper';
import EmployeeCountField from './EmployeeCountField';
import UploadMspLogoStep from './logo/UploadMspLogoStep';
import { useCreateMspMediaFolder } from './useCreateMspMediaFolder';

const CreateMSPFormFields = z.object({
  email: z.string().email('Invalid email'),
  companyInfo: z.object({
    name: z.string().min(1, 'Required'),
    number: z.string().min(1, 'Required'),
    address: Address,
    employeeCount: z.number().min(1),
    vatNumber: z.string().min(1, 'Required'),
    isProductToursEnabled: z.boolean().default(false)
  }),
  region: z.custom<Property>(),
  logo: z.any().optional(),
  allowCustomLogo: z.boolean(),
  haloClientId: z.string().optional().nullable(),
  haloGeneralUserId: z.string().optional().nullable()
});

export type CreateMSPFormFields = z.infer<typeof CreateMSPFormFields>;

const CreateMspPageContent: FunctionComponent = () => {
  const { role } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (role.type !== RoleType.Admin) {
      navigate('-1');
    }
  }, []);

  const methods = useForm<CreateMSPFormFields>({
    resolver: zodResolver(CreateMSPFormFields),
    defaultValues: {
      companyInfo: {
        employeeCount: 1,
        isProductToursEnabled: false
      },
      allowCustomLogo: false,
      haloClientId: '',
      haloGeneralUserId: ''
    }
  });

  const createPartner = useCreatePartner();
  const createPartnerCode = useCreatePartnerCode();
  const invite = useInviteToPartner();
  const uploadLogo = useUploadLogo();

  const createMspMediaFolder = useCreateMspMediaFolder();

  const [regions] = useRegions();
  const regionOptions: Property[] = regions.map(r => ({ id: r.id, name: r.name }));

  const onSubmit = useCallback(async () => {
    const data = methods.getValues();

    const createPartnerCodeResult = await createPartnerCode.mutateAsync({ names: [data.companyInfo.name] });

    const createPartnerResult = await createPartner.mutateAsync({
      name: data.companyInfo.name,
      code: createPartnerCodeResult[0].code,
      regionId: data.region.id,
      registeredInfo: {
        name: data.companyInfo.name,
        number: data.companyInfo.number,
        address: serialiseAddress(data.companyInfo.address),
        employeeCount: data.companyInfo.employeeCount,
        vatNumber: data.companyInfo.vatNumber
      },
      allowCustomLogo: data.allowCustomLogo,
      isProductToursEnabled: data.companyInfo.isProductToursEnabled,
      haloClientId: data.haloClientId || null,
      haloGeneralUserId: data.haloGeneralUserId || null
    });

    if (createPartnerResult.id) {
      try {
        await invite.mutateAsync({
          partnerId: createPartnerResult.id,
          email: data.email
        });

        if (data.logo) {
          await uploadLogo.mutateAsync({ file: data.logo, id: createPartnerResult.id });
        }

        await createMspMediaFolder.mutateAsync({ name: createPartnerResult.name, msp: createPartnerResult.id });
      } catch (error) {
        console.log('Error during sidecar creation');
      }

      navigate('/msps/manageMsps');
      toast(`MSP ${data.companyInfo.name} - successfully created`);
    }
  }, [methods]);

  const getError = () => {
    const createPartnerErrorObj = createPartner.error as Error | undefined;
    const createPartnerCodeErrorObj = createPartnerCode.error as Error | undefined;
    const inviteErrorObj = invite.error as Error | undefined;
    const uploadLogoErrorObj = uploadLogo.error as Error | undefined;

    return createPartnerCodeErrorObj
      ? createPartnerCodeErrorObj.message
      : createPartnerErrorObj
      ? createPartnerErrorObj.message
      : inviteErrorObj
      ? inviteErrorObj.message
      : uploadLogoErrorObj
      ? uploadLogoErrorObj.message
      : '';
  };

  const error = getError();
  const breadcrumbs: NavPage[] = [
    {
      name: 'MSPs',
      to: '/msps/manageMsps'
    }
  ];

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between gap-5 items-center font-bold">
          <h3 className="text-3xl">Create MSP</h3>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto">
        <Container className="w-full py-8">
          <Form className="flex w-full flex-col py-3" methods={methods} onSubmit={() => {}}>
            <Multistep onFinish={onSubmit}>
              <Step
                id="companyInfo"
                title="Company Information"
                validate={async () =>
                  await methods.trigger([
                    'companyInfo.name',
                    'companyInfo.number',
                    'companyInfo.employeeCount',
                    'email'
                  ])
                }
              >
                <div className="flex flex-row space-x-5">
                  <EmailField id="email" className="flex-1" label="Primary contact email address" />
                  <TextField id="companyInfo.vatNumber" label="VAT number" />
                </div>
                <div className="flex flex-row space-x-5">
                  <TextField className="flex-1" id="companyInfo.name" label="Company name" />
                  <TextField id="companyInfo.number" label="Registered company number" />
                </div>
                <div className="flex flex-row space-x-5">
                  <div className="flex-1">
                    <EmployeeCountField />
                  </div>
                  <div className="flex-1">
                    <SelectField id="region" label="Region" options={regionOptions} placeholder="Please Select" />
                  </div>
                </div>
                {role.type === RoleType.Admin && (
                  <>
                    <div className="flex flex-row space-x-5">
                      <div className="flex items-center gap-3 py-4">
                        <p className="text-sm font-medium text-gray-700">Enable Product Tours</p>
                        <Controller
                          control={methods.control}
                          name="companyInfo.isProductToursEnabled"
                          render={({ field }) => (
                            <ToggleInput id="companyInfo.isProductToursEnabled" value={field.value} onChange={field.onChange} />
                          )}
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <TextField id="haloClientId" label="Halo Client ID" />
                    </div>
                    <div className="mt-4">
                      <TextField id="haloGeneralUserId" label="Halo General User ID" />
                    </div>
                  </>
                )}
              </Step>
              <Step id="businessAddress" title="Business Address">
                <AddressField fieldKey="companyInfo" optional={true} />
              </Step>
              <UploadMspLogoStep />
            </Multistep>

            {error && <p className="text-red-500">{error}</p>}
          </Form>
        </Container>
      </div>
    </>
  );
};

const CreateMspPage: FunctionComponent = () => {
  return (
    <>
      <PageTitle>Create MSP</PageTitle>
      <Suspense fallback={<PageLoading />}>
        <CreateMspPageContent />
      </Suspense>
    </>
  );
};

export default CreateMspPage;
